import {gql, QueryResult} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {SelectTemplateDropdownQuery} from 'notebook/entry-templates/__generated/SelectTemplateDropdownQuery';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import sortedBy from 'util/sortedBy';

type SelectTemplateDropdownTemplate = SelectTemplateDropdownQuery['templates'][number];

export const SELECT_TEMPLATE_DROPDOWN_QUERY: DocumentNode = gql`
  query SelectTemplateDropdownQuery @skipAudit @engComponent(name: NOTEBOOK) {
    currentUser {
      ... on UserNode {
        id
        starredTemplates {
          id
        }
      }
    }
    templates {
      id
      name
      templateCollection {
        id
        name
      }
      archiveRecord {
        purpose
      }
    }
  }
`;

interface SelectTemplateDropdownProps {
  isDisabled: boolean;
  selectedTemplateId: string;
  onChangeSelectedTemplate: (template: SelectTemplateDropdownTemplate) => void;
  filterTemplateIds?: Array<string>;
  error?: string;
}

interface SelectTemplateDropdownQueryData {
  starredTemplateIds: Array<string>;
  templates: Array<SelectTemplateDropdownTemplate>;
  refetch: QueryResult['refetch'];
}

function getTemplateDisplayText(template: SelectTemplateDropdownTemplate): string {
  return `${template.templateCollection.name} / ${template.name}`;
}

function makeOptionData(template: SelectTemplateDropdownTemplate): OptionData {
  return {
    name: getTemplateDisplayText(template),
    value: template.id,
    icon: 'template',
    className: 'newDropdown-schemaOption',
    extra: {unstarrable: false},
  };
}

type PresentationalSelectTemplateDropdownProps = SelectTemplateDropdownProps &
  SelectTemplateDropdownQueryData;

function PresentationalSelectTemplateDropdown(props: PresentationalSelectTemplateDropdownProps): JSX.Element {
  let optionDatas = sortedBy(
    props.templates.filter((template) => !isArchived(template)).map((template) => makeOptionData(template)),
    [(option) => (props.starredTemplateIds.includes(option.value) ? `a${option.name}` : `b${option.name}`)]
  );
  if (props?.filterTemplateIds?.length) {
    optionDatas = optionDatas.filter((template) => props.filterTemplateIds!.includes(template.value));
  }
  const selectedTemplate = props.templates.find((template) => template.id === props.selectedTemplateId);
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleChangeStar = async (templateId: string) => {
    if (props.starredTemplateIds.includes(templateId)) {
      await TemplateDAO.unstar([templateId]);
    } else {
      await TemplateDAO.star([templateId]);
    }
    props.refetch();
  };
  return (
    <SelectDropdown
      disabled={props.isDisabled}
      displayName={selectedTemplate ? getTemplateDisplayText(selectedTemplate) : 'Select an Entry template'}
      menu={
        <SearchableMenu
          className="menu--short"
          scrollable={true}
          width={Menu.WIDTHS.FULL}
          onSelect={(templateId: string) => {
            const template = props.templates.find((t) => t.id === templateId)!;
            props.onChangeSelectedTemplate(template);
          }}
          optionDatas={optionDatas}
          renderOptionContent={getRenderStarredOptionContent(handleChangeStar, props.starredTemplateIds, {
            getId: (optionData: OptionData) => optionData.value,
          })}
          selectedValue={props.selectedTemplateId}
        />
      }
      error={props.error}
    />
  );
}

export function useSelectTemplateDropdownQueryData(): SelectTemplateDropdownQueryData {
  const selectTemplateDropdownResult = useBenchlingQuery<SelectTemplateDropdownQuery>(
    SELECT_TEMPLATE_DROPDOWN_QUERY,
    {renderCachedDataWhenRefetching: true, fetchPolicy: 'network-only'}
  );
  if (selectTemplateDropdownResult.type !== QueryResultType.SUCCESS) {
    return {
      starredTemplateIds: [],
      templates: [],
      refetch: selectTemplateDropdownResult.refetch,
    };
  }
  const currentUser = selectTemplateDropdownResult.data.currentUser;
  assert(currentUser?.__typename === 'UserNode', `Non-user should not access this page.`);

  const starredTemplateIds = selectTemplateDropdownResult.data.currentUser
    ? currentUser.starredTemplates.map((template) => template.id)
    : [];
  return {
    starredTemplateIds,
    templates: selectTemplateDropdownResult.data.templates,
    refetch: selectTemplateDropdownResult.refetch,
  };
}

export default function SelectTemplateDropdown(props: SelectTemplateDropdownProps): JSX.Element {
  const selectTemplateDropdownQueryData = useSelectTemplateDropdownQueryData();
  return <PresentationalSelectTemplateDropdown {...props} {...selectTemplateDropdownQueryData} />;
}
